import React, { useMemo, useState } from "react";
import { Paper, Typography, Box, Chip, FormControlLabel, Switch } from "@mui/material";
import Plot from "react-plotly.js";
import { CHART_COLORS, PROFESSIONAL_CHART_COLORS } from "../../utils/constants";
import { chartConfigs } from "../../utils/chartConfigs";
import { useResponsive, getResponsiveChartConfig } from "../../utils/responsive";
import ErrorState from "../common/ErrorState";
import { ChartSkeleton } from "../common/Skeletons";
import useDashboardData from "../../hooks/useDashboardData";

const TimeSeriesByCategory = () => {
  const { data, loading, error, refetch } = useDashboardData('records/aggregate', { groupBy: 'misconduct_event_category,date_of_misconduct_event' });
  const { isMobile, isTablet } = useResponsive();
  const [showAllCategories, setShowAllCategories] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState(new Set());

  // Memoize expensive calculations
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { traces: [], categories: [], dates: [] };

    const categories = [...new Set(data.map(d => d.misconduct_event_category))];
    const dates = [...new Set(data.map(d => d.date_of_misconduct_event))].sort();
    
    // Filter categories based on user selection
    const categoriesToShow = showAllCategories ? categories : 
      categories.filter(cat => selectedCategories.has(cat));
    
    const traces = categoriesToShow.map((cat, i) => ({
      x: dates,
      y: dates.map(date =>
        (data.find(d => d.misconduct_event_category === cat && d.date_of_misconduct_event === date)?.count) || 0
      ),
      type: "scatter",
      mode: "lines+markers",
      name: cat,
      marker: { color: CHART_COLORS[i % CHART_COLORS.length] },
      line: { width: isMobile ? 2 : 3 },
      marker: { size: isMobile ? 4 : 6 }
    }));

    return { traces, categories, dates };
  }, [data, showAllCategories, selectedCategories, isMobile]);

  // Handle category selection
  const toggleCategory = (category) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      newSelected.add(category);
    }
    setSelectedCategories(newSelected);
  };

  // Get responsive chart configuration
  const responsiveConfig = getResponsiveChartConfig(isMobile, isTablet);

  return (
    <Paper sx={{ p: 3, height: "100%" }}>
      <Typography variant="h6" gutterBottom>
        Misconduct Cases Over Time by Category
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Trends in reported misconduct cases, grouped by category.
      </Typography>
      
      {/* Category Controls */}
      {chartData.categories.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAllCategories}
                onChange={(e) => setShowAllCategories(e.target.checked)}
                size="small"
              />
            }
            label="Show all categories"
            sx={{ mb: 1 }}
          />
          
          {!showAllCategories && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chartData.categories.map((category, index) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => toggleCategory(category)}
                  color={selectedCategories.has(category) ? "primary" : "default"}
                  variant={selectedCategories.has(category) ? "filled" : "outlined"}
                  size="small"
                  sx={{ 
                    backgroundColor: selectedCategories.has(category) 
                      ? CHART_COLORS[index % CHART_COLORS.length] 
                      : undefined 
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ minHeight: 400 }}>
        {loading ? (
          <ChartSkeleton height={400} title={false} description={false} />
        ) : error ? (
          <ErrorState height={400} message={error} onRetry={refetch} />
        ) : data.length === 0 ? (
          <ErrorState height={400} message="No data available." severity="info" />
        ) : (
          <Plot
            data={chartData.traces}
            layout={{
              ...chartConfigs.timeSeries.layout,
              ...responsiveConfig.layout,
              autosize: true,
              height: isMobile ? 300 : isTablet ? 350 : 400
            }}
            config={responsiveConfig}
            useResizeHandler={true}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default TimeSeriesByCategory;